'use client'

import { useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'
import { sortBy } from 'lodash'
import { SearchableSelect } from '@/ui'

import {
  ChartOfAccount,
  fetchChartOfAccountsIfNeeded,
} from '@/featuresV1/ChartOfAccounts/chartOfAccount.slice'
import {
  fetchTransactionCategoriesIfNeeded,
  TransactionAccountType,
  TransactionCategory,
} from '@/featuresV1/Reports/reports.slice'
import {
  getChartOfAccountForYear,
  getCurrentChartOfAccount,
} from '@/featuresV1/ChartOfAccounts/chartOfAccount.selectors'
import {
  getAllTransactionCategories,
  mapTransactionCategories,
  selectCategoriesByType,
  selectNonExpenseAndIncomeCategories,
} from '@/featuresV1/Reports/reports.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { TransactionCategoryIdentifier } from '@/reducers/admin/allTransactions.slice'

export interface TransactionCategoryDropdownProps {
  optionStyle: 'normal' | 'combineIdAndValue'
  onChange: (value: number | undefined) => void
  isTaxCompliant?: boolean
  disabled?: boolean
  accountType?: TransactionAccountType
  limitToCategoryNames?: string[]
  limitToCategoryIdentifiers?: TransactionCategoryIdentifier[]
  transactionAmount?: number
  open?: boolean
  transactionDate?: string
  transactionYear?: string
  value?: number
  placeholder?: string
  inputRef?: React.RefObject<HTMLInputElement>
  onBlur?: () => void
}

export const TransactionCategoryDropdown = ({
  disabled,
  onChange,
  isTaxCompliant = false,
  optionStyle,
  value,
  accountType,
  limitToCategoryNames = [],
  limitToCategoryIdentifiers = [],
  transactionAmount,
  transactionDate,
  transactionYear,
  placeholder = 'Select Category',
  inputRef,
  onBlur,
}: TransactionCategoryDropdownProps) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchTransactionCategoriesIfNeeded())
    dispatch(fetchChartOfAccountsIfNeeded())
  }, [dispatch])

  const currentCOA: ChartOfAccount | undefined = useReselector(
    getCurrentChartOfAccount
  )

  const yearString = useMemo(() => {
    if (transactionYear) return transactionYear
    if (transactionDate) {
      return DateTime.fromISO(transactionDate, { zone: 'utc' }).year.toString()
    }
    return undefined
  }, [transactionDate, transactionYear])

  const coaYearOfTransaction: ChartOfAccount | undefined = useReselector(
    getChartOfAccountForYear,
    yearString
  )

  const allTransactionCategories = useReselector(getAllTransactionCategories)
  const expenseCategories = useReselector(selectCategoriesByType, 'Expenses')
  const incomeCategories = useReselector(selectCategoriesByType, 'Income')
  const otherCategories = useReselector(selectNonExpenseAndIncomeCategories)

  const categoriesToDisplay = useMemo(() => {
    // eslint-disable-next-line
    if (transactionAmount != null) {
      return transactionAmount < 0
        ? { ...expenseCategories, ...otherCategories }
        : { ...incomeCategories, ...otherCategories }
    }
    return allTransactionCategories
  }, [
    transactionAmount,
    expenseCategories,
    incomeCategories,
    otherCategories,
    allTransactionCategories,
  ])

  const coaCategories = useMemo(() => {
    const categories: Record<string, TransactionCategory> = {}
    const chartOfAccounts = coaYearOfTransaction || currentCOA
    if (chartOfAccounts) {
      chartOfAccounts.chartOfAccountCategories.forEach((coaCategory) => {
        const lookup = categoriesToDisplay[coaCategory.transactionCategoryId]

        const matchesName =
          limitToCategoryNames.length > 0 &&
          limitToCategoryNames.includes(lookup?.name)

        const matchesIdentifier =
          limitToCategoryIdentifiers.length > 0 &&
          limitToCategoryIdentifiers.includes(lookup?.identifier)

        const matchesAccountType =
          !accountType || lookup?.accountType === accountType

        const shouldInclude =
          (lookup &&
            limitToCategoryNames.length === 0 &&
            limitToCategoryIdentifiers.length === 0 &&
            matchesAccountType) ||
          matchesName ||
          matchesIdentifier

        if (shouldInclude && lookup) {
          categories[lookup.id] = lookup
        }
      })
    }
    return categories
  }, [
    coaYearOfTransaction,
    currentCOA,
    categoriesToDisplay,
    accountType,
    limitToCategoryNames,
    limitToCategoryIdentifiers,
  ])

  const options =
    optionStyle === 'combineIdAndValue'
      ? Object.values(coaCategories).map((category) => ({
          value: category.id,
          label: `${category.id}: ${category.name}`,
          isTaxCompliant,
        }))
      : Object.values(mapTransactionCategories(coaCategories)).map((o) => ({
          value: o.value,
          label: o.text,
        }))

  const valueAsNumber = Number(value)
  const valueExists = options.find((o) => o.value === valueAsNumber)

  const fallbackCategory = !valueExists
    ? categoriesToDisplay[valueAsNumber]
    : null

  const finalOptions = sortBy(
    fallbackCategory
      ? [
          ...options,
          {
            value: fallbackCategory.id,
            label:
              optionStyle === 'combineIdAndValue'
                ? `${fallbackCategory.id}: ${fallbackCategory.name}`
                : fallbackCategory.name,
          },
        ]
      : options,
    'label'
  )

  return (
    <SearchableSelect
      options={finalOptions}
      value={value !== undefined ? String(value) : undefined}
      onChange={(val) => onChange(Number(val))}
      disabled={disabled}
      placeholder={placeholder}
      inputRef={inputRef}
      onBlur={onBlur}
    />
  )
}
